<script setup lang="ts">
import {
  ArrowLeftRight as ArrowLeftRightIcon,
  Loader2 as Loader2Icon,
  Redo as RedoIcon,
  Undo as UndoIcon,
} from '@lucide/vue'
import { computed, ref, watch } from 'vue'

import ElevationProfileChart from '@/components/map/toolbar/ElevationProfileChart.vue'
import MetricSelector from '@/components/map/toolbar/MetricSelector.vue'
import RoutePointList from '@/components/map/toolbar/RoutePointList.vue'
import RouteStatsRow from '@/components/map/toolbar/RouteStatsRow.vue'
import BaseSegmentedControl from '@/components/ui/BaseSegmentedControl.vue'
import BaseSelect from '@/components/ui/BaseSelect.vue'
import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import { ROUTING_PROFILES } from '@/services/routingService'
import {
  floatingSheetBottomOffset,
  mobileSheetFullHeight,
  mobileSheetMaxHeight,
} from '@/services/safeAreaService'
import { useUiStore } from '@/stores/uiStore'
import type { Tour } from '@/types/tour'
import type {
  TrackProfileAnalyses,
  TrackProfileLocationMatch,
  TrackProfileMetricId,
  TrackProfileVisibleRange,
} from '@/utils/trackProfileAnalysis'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'TrackPlanningSheet', inheritAttrs: false })

type PlanningTab = 'details' | 'profile' | 'points'
type ProfileBreakdownMetricId = 'surface' | 'wayType'
type ProfileSection = { metricId: ProfileBreakdownMetricId; categoryId: string }

const props = withDefaults(
  defineProps<{
    /** Drawing a fresh track vs editing an existing one — changes copy + the append-direction control. */
    mode: 'drawing' | 'edit'
    /** Draft / editing tour whose route points + segments drive the sheet. */
    tour: Tour | null
    /** Active track being planned, scopes the route-point list + stats. */
    trackClientId: string | null
    /** Number of map anchors in a fresh drawing, including the unpersisted seed point. */
    drawingPointCount?: number
    analyses: TrackProfileAnalyses
    canReverse: boolean
    canUndo: boolean
    canRedo: boolean
    canSave: boolean
    isCalculatingRoute: boolean
    isBusy?: boolean
    statusText?: string | null
    profileMetric?: TrackProfileMetricId
    activeMetric?: ProfileBreakdownMetricId | null
    highlightedCategoryId?: string | null
    cursorDistanceM?: number | null
    visibleRangeM?: TrackProfileVisibleRange | null
    currentLocation?: TrackProfileLocationMatch | null
    interactionResetKey?: number
  }>(),
  {
    isBusy: false,
    statusText: null,
    profileMetric: 'gradient',
    activeMetric: null,
    highlightedCategoryId: null,
    cursorDistanceM: null,
    visibleRangeM: null,
    currentLocation: null,
    interactionResetKey: 0,
    drawingPointCount: 0,
  },
)

const emit = defineEmits<{
  reverse: []
  undo: []
  redo: []
  save: []
  'routing-profile-change': [profile: string]
  'profile-metric-change': [metric: TrackProfileMetricId]
  'zoom-route-point': [tourId: string, routePointIndex: number]
  'reorder-route-points': [
    tourId: string,
    trackClientId: string | null,
    orderedRoutePointClientIds: string[],
  ]
  'update:cursorDistanceM': [value: number | null]
  'update:visibleRangeM': [value: TrackProfileVisibleRange | null]
  'hover:section': [value: ProfileSection | null]
  'cycle:section': [value: ProfileSection]
  'leave:profile': []
  'update:height': [height: number]
}>()

const uiStore = useUiStore()

const prefersReducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

// Tab order mirrors the saved-route sheet (Details · Profile · Waypoints) so the planner feels like
// the same surface — "Route Points" takes the place of "Waypoints". Drawing starts on Details so
// routing preferences are visible before the first map point is added.
// "Points" (not "Route Points") keeps the 3-tab segmented control to one line at 393px; the list
// header below still reads "Route Points (n)" so the full term is present.
const TABS: Array<{ id: PlanningTab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'profile', label: 'Profile' },
  { id: 'points', label: 'Points' },
]
const activeTab = ref<PlanningTab>('details')
const bodyRef = ref<HTMLElement | null>(null)

const isInteractionDisabled = computed(() => props.isBusy || props.isCalculatingRoute)

// The active track's stats subset, so RouteStatsRow shows the same headline as the saved-route sheet.
const statsTracks = computed(() => {
  const tour = props.tour
  if (!tour) return []
  if (!props.trackClientId) return tour.tracks
  const track = tour.tracks.find((t) => t.clientId === props.trackClientId)
  return track ? [track] : tour.tracks
})
const routePointCount = computed(() => {
  const tour = props.tour
  if (!tour) return 0
  return props.trackClientId
    ? tour.routePoints.filter((rp) => rp.trackClientId === props.trackClientId).length
    : tour.routePoints.length
})
const hasPoints = computed(() => routePointCount.value > 0)

const tabOptions = computed(() =>
  TABS.map((tab) => ({
    value: tab.id,
    label: tab.label,
    badge: tab.id === 'points' && routePointCount.value > 0 ? routePointCount.value : null,
    testId: `planning-tab-${tab.id}`,
  })),
)

const routingProfileOptions = computed(() =>
  ROUTING_PROFILES.map((p) => ({ id: p.id, name: p.label })),
)
const appendDirectionOptions = [
  { id: 'end', name: 'Append to end' },
  { id: 'start', name: 'Prepend to start' },
]

const metricModel = computed({
  get: () => props.profileMetric,
  set: (value: TrackProfileMetricId) => emit('profile-metric-change', value),
})
const showChart = computed(() => props.analyses.gradient.points.length > 1)

const viewportHeight = ref(typeof window === 'undefined' ? 844 : window.innerHeight)
// Match the saved-route sheet's snap rhythm so the elevation chart is framed identically: the detail
// sheet's mid is 330 with no footer; the planner adds a ~65px action footer, so its mid is that much
// taller (≈395) to leave the same body height above the footer for the chart. Keeps "not too high".
const PLANNING_MID_PX = 395
const sheetHeights = computed<Record<'peek' | 'mid' | 'full', number>>(() => {
  const full = mobileSheetFullHeight(viewportHeight.value)
  return {
    peek: 80,
    mid: Math.min(PLANNING_MID_PX, full - 40),
    full,
  }
})
const sheet = useBottomSheet({
  modes: ['peek', 'mid', 'full'] as const,
  heights: sheetHeights,
  initialMode: 'mid',
})

const showBody = computed(() => sheet.mode.value !== 'peek')
const selectTab = (tab: PlanningTab) => {
  if (tab === activeTab.value) return
  activeTab.value = tab
  if (bodyRef.value) bodyRef.value.scrollTop = 0
  if (sheet.mode.value === 'peek') sheet.snapTo('mid')
}

watch(
  () => props.drawingPointCount,
  (count, previousCount) => {
    if (props.mode === 'drawing' && count > previousCount) {
      selectTab('points')
    }
  },
)

// Planning has no bottom tab bar, so sit just above the home indicator (tabBarPx = 0) — the same
// resting position as the saved-route sheet at a detail level. (We don't use the injected offset
// because drawing/edit isn't a "detail level", which would otherwise reserve the hidden bar's space.)
const planningSheetBottom = floatingSheetBottomOffset(0)
const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(planningSheetBottom),
  bottom: planningSheetBottom,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition:
    sheet.isDragging.value || prefersReducedMotion
      ? 'none'
      : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

useEventListener(window, 'resize', () => {
  viewportHeight.value = window.innerHeight
})

const settledHeight = computed(() => sheetHeights.value[sheet.mode.value])
watch(settledHeight, (height) => emit('update:height', height), { immediate: true })
</script>

<template>
  <div
    class="pointer-events-auto fixed left-3 right-3 flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white/95 shadow-[0_-8px_30px_rgba(15,23,42,0.14)] backdrop-blur-xl"
    :style="sheetStyle"
    data-testid="track-planning-sheet"
  >
    <!-- Persistent header (drag area): handle · live stats · tabs (expanded only). Mirrors the
         saved-route sheet's header so the two feel identical. -->
    <div
      class="shrink-0 touch-none px-4 pt-2 pb-1"
      :class="sheet.isDragging.value ? 'cursor-grabbing' : 'cursor-grab'"
      data-testid="track-planning-sheet-handle"
      @pointerdown="sheet.onPointerDown"
      @pointermove="sheet.onPointerMove"
      @pointerup="sheet.onPointerUp"
      @pointercancel="sheet.onPointerUp"
    >
      <div class="mb-1.5 flex justify-center">
        <div class="h-1.5 w-10 rounded-full bg-neutral-300"></div>
      </div>

      <RouteStatsRow v-if="hasPoints && tour" :tour="tour" :tracks="statsTracks" class="mt-1" />
      <p v-else class="py-1 text-center text-sm font-medium text-neutral-500">
        {{
          mode === 'drawing' ? 'Tap the map to start your route' : 'Tap the map to extend the track'
        }}
      </p>

      <BaseSegmentedControl
        v-show="showBody"
        class="mt-2"
        variant="tabs"
        :model-value="activeTab"
        :options="tabOptions"
        aria-label="Route planning tabs"
        option-id-prefix="planning-tab"
        panel-id-prefix="planning-panel"
        @update:model-value="selectTab($event as PlanningTab)"
      />
    </div>

    <!-- Body -->
    <div ref="bodyRef" v-show="showBody" class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <!-- Details (preferences) -->
      <div
        v-show="activeTab === 'details'"
        id="planning-panel-details"
        role="tabpanel"
        aria-labelledby="planning-tab-details"
        class="space-y-4 px-4 py-3"
      >
        <!-- Edit mode only (matches desktop): where new map taps attach — append to end / prepend to
             start. A fresh draw builds A→B sequentially, so it has nothing to prepend to. -->
        <BaseSelect
          v-if="mode === 'edit'"
          label="Add points to"
          :model-value="uiStore.editingTrackAppendDirection"
          :options="appendDirectionOptions"
          :disabled="isInteractionDisabled"
          @update:model-value="uiStore.setEditingTrackAppendDirection($event as 'end' | 'start')"
        />

        <BaseSelect
          label="Routing profile"
          :model-value="uiStore.routingProfile"
          :options="routingProfileOptions"
          :disabled="isInteractionDisabled"
          @update:model-value="emit('routing-profile-change', String($event))"
        />

        <BaseSegmentedControl
          variant="compact"
          label="Color route by"
          aria-label="Color route by"
          :model-value="profileMetric"
          :options="[
            { value: 'gradient', label: 'Gradient' },
            { value: 'surface', label: 'Surface' },
            { value: 'wayType', label: 'Way type' },
          ]"
          :disabled="isInteractionDisabled"
          @update:model-value="emit('profile-metric-change', $event as TrackProfileMetricId)"
        />
      </div>

      <!-- Profile -->
      <div
        v-show="activeTab === 'profile'"
        id="planning-panel-profile"
        role="tabpanel"
        aria-labelledby="planning-tab-profile"
        class="space-y-2 px-4 py-2"
      >
        <template v-if="showChart">
          <div class="-mb-1">
            <MetricSelector v-model="metricModel" align="left" />
          </div>
          <ElevationProfileChart
            tall
            :analyses="analyses"
            :metric="profileMetric"
            :cursor-distance-m="cursorDistanceM"
            :visible-range-m="visibleRangeM"
            :active-metric="activeMetric"
            :highlighted-category-id="highlightedCategoryId"
            :current-location="currentLocation"
            :interaction-reset-key="interactionResetKey"
            @update:cursor-distance-m="emit('update:cursorDistanceM', $event)"
            @update:visible-range-m="emit('update:visibleRangeM', $event)"
            @hover:section="emit('hover:section', $event)"
            @cycle:section="emit('cycle:section', $event)"
            @leave:profile="emit('leave:profile')"
          />
        </template>
        <div
          v-else
          role="status"
          class="flex flex-col items-center gap-1 py-8 text-center text-sm text-neutral-500"
        >
          <span>No elevation data yet</span>
        </div>
      </div>

      <!-- Route Points -->
      <div
        v-show="activeTab === 'points'"
        id="planning-panel-points"
        role="tabpanel"
        aria-labelledby="planning-tab-points"
        class="py-1"
      >
        <RoutePointList
          v-if="tour && hasPoints"
          :tour="tour"
          :track-client-id="trackClientId"
          :expanded="true"
          :can-delete-route-points="true"
          :reorderable="true"
          :hide-track-headers="true"
          size="comfortable"
          @zoom-route-point="(tourId, index) => emit('zoom-route-point', tourId, index)"
          @reorder-route-points="
            (tourId, track, ids) => emit('reorder-route-points', tourId, track, ids)
          "
        />
        <div
          v-else
          role="status"
          class="flex flex-col items-center gap-1 px-4 py-10 text-center text-sm text-neutral-500"
        >
          <span>{{
            mode === 'drawing'
              ? 'Tap the map to add route points'
              : 'Tap the map to extend the track'
          }}</span>
        </div>
      </div>
    </div>

    <!-- Status line while routing -->
    <div
      v-show="showBody && (isBusy || isCalculatingRoute) && statusText"
      class="flex shrink-0 items-center justify-center gap-2 border-t border-gray-100 bg-emerald-50 px-4 py-1.5 text-xs font-medium text-emerald-700"
    >
      <Loader2Icon class="h-4 w-4 animate-spin" />
      <span>{{ statusText }}</span>
    </div>

    <!-- Action footer (expanded only): undo / redo / reverse · Save. Cancel/close lives in the
         floating top-left button (with a confirm), so the footer can give Save the prominence. -->
    <div
      v-show="showBody"
      class="flex shrink-0 items-center gap-2 border-t border-gray-200/80 px-3 py-2.5"
    >
      <div class="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          class="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 hover:cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="!canUndo || isInteractionDisabled"
          title="Undo"
          aria-label="Undo"
          @click="emit('undo')"
        >
          <UndoIcon class="h-5 w-5" />
        </button>
        <button
          type="button"
          class="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 hover:cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="!canRedo || isInteractionDisabled"
          title="Redo"
          aria-label="Redo"
          @click="emit('redo')"
        >
          <RedoIcon class="h-5 w-5" />
        </button>
        <button
          type="button"
          class="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200 hover:cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          :disabled="!canReverse || isInteractionDisabled"
          title="Reverse route"
          aria-label="Reverse route"
          @click="emit('reverse')"
        >
          <ArrowLeftRightIcon class="h-5 w-5" />
        </button>
      </div>

      <button
        type="button"
        class="h-11 flex-1 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-700 hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canSave || isInteractionDisabled"
        data-testid="planning-save"
        @click="emit('save')"
      >
        Save
      </button>
    </div>
  </div>
</template>
